import { Injectable, inject } from '@angular/core';
import {NetworkService} from '../../../../../../projects/common/src/lib/services/network.service';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';
import {Currency} from '../../../enums/currency';
import {BillingPlan} from '../../../model/billing-plan';

export interface ValidateTaxInfoResult {
  valid: boolean;
  error: any;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionApiService extends BaseApiService {

  constructor() {

    super();
  }

  subscribeTrial() {
    return this.post('/subscription/trial', null);
  }

  subscribe(data) {
    return this.post('/subscription/new', data);
  }

  updateBillingInfo() {
    return this.put('/subscription/billing/update', {});
  }

  /**
   * Who the invoices are made out to, and/or the buyer's document (null
   * removes it): checked, sent to Stripe and stored by the API in one call.
   * Refused with tax_id_invalid, billing_country_invalid, invalid_email_address
   * or billing_sync_failed (nothing saved).
   */
  saveBillingDetails(body: {billingInfo?: any, taxId?: {type: string, value: string} | null}) {
    return this.put<{billingInfo: any, taxIdRegistered: boolean}>('/subscription/billing', body);
  }

  updateTaxInfo(taxInfo: any) {
    return this.post('/subscription/tax', {
      taxInfo: taxInfo
    });
  }

  updateSubscription(data: any) {
    return this.put('/subscription/update', data);
  }

  /**
   * Pauses or resumes billing. Paused periods are not invoiced at all rather
   * than accrued, so resuming does not present a bill for the time off. Resuming
   * after the paid period ran out starts a new period today (`restarted`), and
   * `payment` says what happened to its charge (3-D Secure to finish, or declined).
   */
  setPaused(paused: boolean, resumesAt = 0) {
    return this.put<{paused: boolean, resumesAt: number, restarted?: boolean,
      payment?: {status: string, kind?: string, client_secret?: string, code?: string, amount?: number} | null}>(
      '/subscription/paused', {paused, resumesAt});
  }

  /** Sets the Enterprise seat count. Prorates immediately. */
  setSeats(quantity: number) {
    return this.put<{quantity: number, usedSeats: number, minSeats: number, maxSeats: number,
      payment?: {status: string, kind?: string, client_secret?: string, code?: string} | null}>(
      '/subscription/seats', {quantity});
  }

  deleteTaxInfo() {
    return this.delete('/subscription/tax', {});
  }

  getAllPlans(currency?: Currency): Promise<any> {
    let url = '/subscription/plans';
    if (currency) {
      url += `?currency=${currency}`;
    }
    return this.get(url);
  }

  getPlanDetails(planId: string, gateway: string, currency: Currency): Promise<BillingPlan> {
    let url = `/subscription/plans/${planId}?gateway=${gateway}`;
    if (currency) {
      url += `&currency=${currency}`;
    }
    return this.get(url);
  }

  /** The dashboard's one-time window about a price change was closed: not shown again, on any device. */
  markPriceChangeSeen(): Promise<{seen: boolean}> {
    return this.post('/subscription/price-change/seen', {});
  }

  /** Plan changes, cancellations, pauses and failed payments, newest first. */
  getHistory(): Promise<{events: any[]}> {
    return this.get('/subscription/history');
  }

  /** Undo a scheduled cancellation: the subscription carries on renewing. */
  keepSubscription(): Promise<{kept: boolean}> {
    return this.post('/subscription/keep', {});
  }

  cancelSubscription(reason: string) {
    return this.delete('/subscription', {
      reason
    });
  }

  cancelSubscriptionAdmin(targetUid: string, reason: string) {
    return this.delete('/admin/subscription', {
      targetUid,
      reason
    });
  }
}
